import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useZoomStore } from '@/shared/store/zoomStore';
import { pageHeadingFor } from './nav-config';

// Every ERP page has exactly one h1. Most pages show their title in the sub-nav
// tab only (no visible heading, by design), so the shell renders a visually
// hidden h1 named after the record, tab or section. A page that renders its own
// h1 (PageHeader with a title, a few bespoke pages) claims the heading and the
// shell's one steps aside.
type Claims = { claim: () => () => void };
const ClaimContext = createContext<Claims | null>(null);
const ClaimCount = createContext(0);

export function PageHeadingProvider({ children }: { children: ReactNode }) {
  const [count, setCount] = useState(0);
  const [claims] = useState<Claims>(() => ({
    claim: () => {
      setCount((c) => c + 1);
      return () => setCount((c) => c - 1);
    },
  }));
  return (
    <ClaimContext.Provider value={claims}>
      <ClaimCount.Provider value={count}>{children}</ClaimCount.Provider>
    </ClaimContext.Provider>
  );
}

// Render next to a page's own <h1>. Outside the shell (a test, a modal page
// rendered alone) there is nothing to claim.
export function PageHeadingClaim(): null {
  const claims = useContext(ClaimContext);
  useEffect(() => (claims ? claims.claim() : undefined), [claims]);
  return null;
}

export function RouteHeading() {
  const { pathname } = useLocation();
  const currentLabel = useZoomStore((s) => s.currentLabel);
  const claimed = useContext(ClaimCount) > 0;
  if (claimed) return null;
  return <h1 className="sr-only">{pageHeadingFor(pathname, currentLabel)}</h1>;
}
